// home.component.ts
import { Component, HostListener, inject, type OnInit } from '@angular/core';
import { Clock4, ClockArrowDown, LucideAngularModule, PartyPopper } from 'lucide-angular';
import { AuthService } from '../../core/auth/services/auth.service';
import type { HighlightItem } from '../../core/models/highlight-item.model';
import type { OverviewItemModel } from '../../core/models/middleware/overview-item.model';
import type { UserItem } from '../../core/models/middleware/user-item.model';
import { EventService } from '../../core/services/event.service';
import { HighlightService } from '../../core/services/highlight.service';
import { isNow } from '../../core/utils/date.util';
import { EVENT_TYPE_FILTERS, type EventTypeFilter, filterEventsByType } from '../../core/utils/event-category.util';
import { EventDatePipe } from '../../shared/pipes/event-date.pipe';
import { EventLocationPipe } from '../../shared/pipes/event-location.pipe';
import { CategoryNavigationComponent } from './category-navigation/category-navigation.component';
import { EventCardComponent } from './event-card/event-card.component';
import { HighlightTeaserComponent } from './highlight-teaser/highlight-teaser.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    LucideAngularModule,
    EventCardComponent,
    EventDatePipe,
    EventLocationPipe,
    CategoryNavigationComponent,
    HighlightTeaserComponent,
  ],
  templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit {
  user?: UserItem;
  highlightItem?: HighlightItem;
  upcomingEvents: OverviewItemModel[] = [];
  allUpcomingEvents: OverviewItemModel[] = [];
  activeEventType: EventTypeFilter = 'Alle';

  private touchStartX = 0;
  private touchEndX = 0;
  private minSwipeDistance = 50;

  protected readonly Clock4 = Clock4;
  protected readonly PartyPopper = PartyPopper;
  protected readonly ClockArrowDown = ClockArrowDown;

  private authService = inject(AuthService);
  private eventService = inject(EventService);
  private highlightService = inject(HighlightService);

  ngOnInit(): void {
    this.user = this.authService.currentUser() || undefined;
    this.eventService.clearCurrentEvent();
    this.loadEvents();
    this.loadHighlight();
  }

  loadEvents(): void {
    this.eventService.getEventOverview(true).subscribe({
      next: (events) => {
        this.allUpcomingEvents = events;
        this.applyFilters();
      },
      error: (error) => {
        console.error('Error fetching events:', error);
        this.allUpcomingEvents = [];
        this.upcomingEvents = [];
        throw error;
      },
    });
  }

  loadHighlight(): void {
    this.highlightService.getHighlightedEvent().subscribe({
      next: (highlight) => {
        this.highlightItem = highlight;
      },
      error: (error) => {
        console.error('Error fetching highlight:', error);
        this.highlightItem = undefined;
      },
    });
  }

  isAnEventNow(event: OverviewItemModel): boolean {
    return event ? isNow(event.startDate.toString(), event.endDate.toString()) : false;
  }

  onCategoryChanged(eventType: EventTypeFilter): void {
    this.activeEventType = eventType;
    this.applyFilters();
  }

  private applyFilters(): void {
    const filteredEvents = filterEventsByType(this.allUpcomingEvents, this.activeEventType);

    this.upcomingEvents = this.allUpcomingEvents.filter((event) =>
      filteredEvents.some((filtered) => filtered === event)
    );
    // .slice(0, 5);
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;

    if (Math.abs(swipeDistance) < this.minSwipeDistance) {
      return;
    }

    const currentIndex = EVENT_TYPE_FILTERS.indexOf(this.activeEventType);

    if (swipeDistance > 0) {
      // Swipe right - go to previous category
      const previousIndex = currentIndex > 0 ? currentIndex - 1 : EVENT_TYPE_FILTERS.length - 1;
      this.onCategoryChanged(EVENT_TYPE_FILTERS[previousIndex]);
    } else {
      // Swipe left - go to next category
      const nextIndex = currentIndex < EVENT_TYPE_FILTERS.length - 1 ? currentIndex + 1 : 0;
      this.onCategoryChanged(EVENT_TYPE_FILTERS[nextIndex]);
    }
  }
}
